import {
  ChangeDetail,
  IChangeHistoryEntry,
  LifecycleManagementAllEvents,
  LifecycleManagementEventStatusType,
  LifecycleManagementEventStrategyType,
  LifecycleManagementUpsertEventRequestDto,
} from "@lib/ShiOneClient";
import { EnrichedActivity, FilteredActivityList } from "shared-ui";
import {
  isStatusChangeWithoutPreviousValue,
  normalizeLifecycleStatus,
  normalizeLifecycleStrategy,
} from "shared-ui/src/components/lifecycle-management/lifecycleManagementEnums";
import EmployeeAvatar, { AvatarSize } from "@components/CxEmployeeAvatar";
import {
  ComponentType,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import NotesIcon from "@mui/icons-material/Notes";
import SignpostIcon from "@mui/icons-material/Signpost";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import EventIcon from "@mui/icons-material/Event";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import shiColors, { graphColors } from "shared-ui/src/theme/shiColors";
import { useLifecycleActivityApi } from "./hooks/useLifecycleActivityApi";
import { DateTime } from "luxon";
import useUpdateAssetLifecycleEventData from "./hooks/useUpdateAssetLifecycleEventsData";
import { useQueryClient } from "@tanstack/react-query";
import useGetAssetLifecycleManagementCustomerOwners from "./hooks/useGetAssetLifecycleManagementCustomerOwners";
import {
  mapAssignedToToPeopleOption,
  mapCustomerOwnerToPeopleOption,
} from "shared-ui/src/components/lifecycle-management/peopleOptionMapper";

export type ActivityLogEntry = IChangeHistoryEntry &
  EnrichedActivity & {
    friendlyPropertyName: string;
  };

const PROPERTY_NAME_TO_FRIENDLY_NAME: Record<string, string> = {
  customerfacingnotes: "Notes",
  status: "Status",
  strategy: "Strategy",
  date: "Date",
  manualoverrideeventdate: "Event",
  assignedto: "Assigned To",
  customerowner: "Customer Owner",
};

const PEOPLE_ACTIVITY_FIELD_KEYS = new Set(["assignedto", "customerowner"]);

const getFriendlyName = (key: string | undefined): string => {
  const trimmedKey = key?.trim();
  if (!trimmedKey) return "Update";
  const lowerKey = trimmedKey.toLowerCase();
  return (
    PROPERTY_NAME_TO_FRIENDLY_NAME[lowerKey] ||
    trimmedKey.charAt(0).toUpperCase() + trimmedKey.slice(1)
  );
};

const getPeopleActivityIdentifiers = (value: unknown): string[] =>
  (Array.isArray(value) ? value : String(value ?? "").split(","))
    .map((identifier) => String(identifier).trim())
    .filter(Boolean);

// Assigned To is read-only in CX
const isAssignedToKey = (key: string | undefined): boolean =>
  key?.trim().toLowerCase() === "assignedto";

export const formatPeopleActivityValue = (
  key: string | undefined,
  value: unknown,
  peopleDisplayNames: Readonly<Record<string, string>>,
): unknown => {
  if (
    !key ||
    value === null ||
    value === undefined ||
    !PEOPLE_ACTIVITY_FIELD_KEYS.has(key.trim().toLowerCase())
  ) {
    return value;
  }

  return (
    getPeopleActivityIdentifiers(value)
      .map(
        (identifier) => peopleDisplayNames[identifier.toLowerCase()] ?? "N/A",
      )
      .join(", ") || value
  );
};

// Categorical slots from the shared graph palette; holes skip entries unused here.
const [
  ACTIVITY_BLUE,
  ,
  ACTIVITY_GREEN,
  ACTIVITY_ORANGE,
  ,
  ,
  ACTIVITY_BLUE_DARK,
  ACTIVITY_PURPLE,
  ,
  ACTIVITY_TURQUOISE,
] = graphColors;

const getEventIconConfig = (
  key: string | undefined,
): { Icon: ComponentType<any>; color: string } => {
  switch (key?.trim().toLowerCase()) {
    case "customerfacingnotes":
      return { Icon: NotesIcon, color: ACTIVITY_ORANGE };
    case "status":
      return { Icon: AdsClickIcon, color: ACTIVITY_PURPLE };
    case "strategy":
      return { Icon: SignpostIcon, color: ACTIVITY_BLUE_DARK };
    case "manualoverrideeventdate":
      return { Icon: EventIcon, color: ACTIVITY_BLUE };
    case "assignedto":
      return { Icon: GroupsOutlinedIcon, color: ACTIVITY_TURQUOISE };
    case "customerowner":
      return { Icon: PersonIcon, color: ACTIVITY_GREEN };
    default:
      return { Icon: SettingsIcon, color: shiColors.gray.light };
  }
};

const strategyStringToEnum = (
  value: string | null | undefined,
): LifecycleManagementEventStrategyType | string | null | undefined =>
  value
    ? normalizeLifecycleStrategy<LifecycleManagementEventStrategyType>(value)
    : value;

const statusStringToEnum = (
  value: string | null | undefined,
): LifecycleManagementEventStatusType =>
  normalizeLifecycleStatus<LifecycleManagementEventStatusType>(
    value,
    LifecycleManagementEventStatusType.NotActioned,
  ) ?? LifecycleManagementEventStatusType.NotActioned;

export default function AssetLifecycleManagementDrawerActivityDetails({
  selectedLifecycleEventData,
  dateFrom,
  dateTo,
  userFilter,
  eventFilter,
  setSelectedLifecycleEventData,
}: Readonly<{
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  dateFrom: DateTime | null;
  dateTo: DateTime | null;
  userFilter: string;
  eventFilter: string;
  setSelectedLifecycleEventData?: Dispatch<
    SetStateAction<LifecycleManagementAllEvents | undefined>
  >;
}>) {
  const assetUniqueId = selectedLifecycleEventData?.id || "";

  const { isLoading, activities, assignees = [] } = useLifecycleActivityApi(
    selectedLifecycleEventData,
    dateFrom,
    dateTo,
  );
  const { data: customerOwnersData } =
    useGetAssetLifecycleManagementCustomerOwners(
      selectedLifecycleEventData?.crmAccountId?.trim(),
    );

  const peopleDisplayNames = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        [
          ...(selectedLifecycleEventData?.assignedTo ?? []).map(
            mapAssignedToToPeopleOption,
          ),
          ...(customerOwnersData ?? []).map(mapCustomerOwnerToPeopleOption),
          ...assignees.map(mapAssignedToToPeopleOption),
        ].map(({ id, displayName }) => [
          id.toLowerCase(),
          displayName || "N/A",
        ]),
      ),
    [customerOwnersData, selectedLifecycleEventData?.assignedTo, assignees],
  );

  const queryClient = useQueryClient();
  const { mutateAsync } = useUpdateAssetLifecycleEventData();
  const [pendingActivityId, setPendingActivityId] = useState<string | null>(
    null,
  );

  const handleRevert = useCallback(
    async (_: string, __: any, uniqueId: string | undefined) => {
      const activity = activities.find((a) => a.id === uniqueId);
      if (
        !activity?.change?.key ||
        activity.change.oldValue === undefined ||
        !assetUniqueId
      )
        return;

      if (isAssignedToKey(activity.change.key)) return;
      if (
        isStatusChangeWithoutPreviousValue(
          activity.change.key,
          activity.change.oldValue,
        )
      )
        return;

      const fieldKey = activity.change.key.trim().toLowerCase();
      const revertValue = activity.change.oldValue;

      const dtoFieldKey =
        fieldKey === "manualoverrideeventdate" ||
        fieldKey === "manualoverridedate"
          ? "manualOverrideEventDate"
          : fieldKey === "customerowner"
            ? "customerOwner"
            : fieldKey === "customerfacingnotes"
              ? "customerFacingNotes"
              : fieldKey;

      const convertedRevertValue =
        fieldKey === "strategy"
          ? strategyStringToEnum(revertValue)
          : fieldKey === "status"
            ? statusStringToEnum(revertValue)
            : fieldKey === "customerowner"
              ? getPeopleActivityIdentifiers(revertValue)
              : revertValue;

      setPendingActivityId(activity.id ?? null);
      try {
        await mutateAsync({
          uniqueId: assetUniqueId,
          [dtoFieldKey]: convertedRevertValue,
          initialValue: {
            uniqueIdentifier: assetUniqueId,
            // The displayed status may be derived; only the stored manual status is a baseline.
            status: selectedLifecycleEventData?.manualStatus
              ? statusStringToEnum(selectedLifecycleEventData.manualStatus)
              : undefined,
            strategy:
              strategyStringToEnum(selectedLifecycleEventData?.strategy) ??
              undefined,
            customerFacingNotes:
              selectedLifecycleEventData?.customerFacingNotes ?? undefined,
            manualOverrideEventDate:
              selectedLifecycleEventData?.eventDate ?? undefined,
            customerOwner: selectedLifecycleEventData?.customerOwner,
          } as LifecycleManagementUpsertEventRequestDto,
        });

        await queryClient.invalidateQueries({
          queryKey: [
            "lifecycle-activity",
            selectedLifecycleEventData?.id,
            dateFrom?.toISO(),
            dateTo?.toISO(),
          ],
        });

        setSelectedLifecycleEventData?.((prev: any) => {
          if (!prev) return prev;
          switch (fieldKey) {
            case "status": {
              const revertedStatus = statusStringToEnum(revertValue);
              return {
                ...prev,
                status: revertedStatus,
                manualStatus: revertedStatus,
              };
            }
            case "strategy":
              return { ...prev, strategy: revertValue };
            case "customerfacingnotes":
              return { ...prev, customerFacingNotes: revertValue };
            case "customerowner":
              return {
                ...prev,
                customerOwner: getPeopleActivityIdentifiers(revertValue),
              };
            case "manualoverrideeventdate":
            case "manualoverridedate": {
              // Parse as UTC (how the API stores it) then convert to local for the datepicker
              const parsed = DateTime.fromFormat(revertValue, "MM/dd/yyyy", {
                zone: "utc",
              }).isValid
                ? DateTime.fromFormat(revertValue, "MM/dd/yyyy", {
                    zone: "utc",
                  })
                : DateTime.fromISO(revertValue, { zone: "utc" }).isValid
                  ? DateTime.fromISO(revertValue, { zone: "utc" })
                  : DateTime.fromFormat(revertValue, "MMMM d, yyyy", {
                      zone: "utc",
                    });
              const normalized = parsed.isValid
                ? parsed.toFormat("MM/dd/yyyy")
                : revertValue;
              return { ...prev, eventDate: normalized ?? undefined };
            }
            default:
              return prev;
          }
        });
      } finally {
        setPendingActivityId(null);
      }
    },
    [
      activities,
      assetUniqueId,
      selectedLifecycleEventData,
      mutateAsync,
      queryClient,
      setSelectedLifecycleEventData,
      dateFrom,
      dateTo,
    ],
  );

  const enrichedActivities = useMemo<ActivityLogEntry[]>(() => {
    const filtered = activities.filter((item) => {
      const userFilterMatch = userFilter
        ? item.changedBy?.actor?.uri === userFilter
        : true;

      if (!eventFilter) {
        return userFilterMatch;
      }

      const itemKey = (item.change?.key || "").trim().toLowerCase();
      const filterKey = eventFilter.trim().toLowerCase();
      const eventFilterMatch = itemKey === filterKey;

      return userFilterMatch && eventFilterMatch;
    });

    // EmployeeAvatar cannot be in used from shared-ui
    return filtered.map((activity: IChangeHistoryEntry): ActivityLogEntry => {
      const { Icon, color } = getEventIconConfig(activity.change?.key);
      const actorEntity = activity.changedBy?.actor;
      const entraId =
        actorEntity?.uriProtocol === "entra" ? actorEntity.uriValue : "";
      const avatarElement = activity.changedBy ? (
        <EmployeeAvatar
          photoSize={AvatarSize.medium}
          entraId={entraId || ""}
          displayName={actorEntity?.displayName || ""}
        />
      ) : undefined;

      return {
        ...activity,
        changedBy: {
          ...activity.changedBy,
          fullName: actorEntity?.displayName,
          emailAddress: actorEntity?.emailAddress,
          entraId,
        } as any,
        change: new ChangeDetail({
          ...activity.change,
          oldValue: formatPeopleActivityValue(
            activity.change?.key,
            activity.change?.oldValue,
            peopleDisplayNames,
          ),
          newValue: formatPeopleActivityValue(
            activity.change?.key,
            activity.change?.newValue,
            peopleDisplayNames,
          ),
        }),
        avatarElement,
        eventIcon: Icon,
        eventColor: color,
        friendlyPropertyName: getFriendlyName(activity.change?.key),
        showRevert:
          !isAssignedToKey(activity.change?.key) &&
          !isStatusChangeWithoutPreviousValue(
            activity.change?.key,
            activity.change?.oldValue,
          ),
      };
    });
  }, [activities, userFilter, eventFilter, peopleDisplayNames]);

  return (
    <FilteredActivityList
      activities={enrichedActivities}
      isLoading={isLoading}
      handleRevert={handleRevert}
      pendingRevertId={pendingActivityId}
    />
  );
}
